import React from "react"
import { Box, Text, VStack, Flex, Spinner, HStack } from "@chakra-ui/react"
import { QuestionBubble } from "./QuestionBubble"
import { usePreClassQuestions } from "./usePreClassQuestions"
import { c } from "../classroomColors"

/**
 * Teacher side of the pre-class Q&A (Figma: Pre-Class-teacher). Every student
 * question as a bubble with its answered status; unanswered ones first, each
 * expandable to write the answer.
 */
export function PreClassTeacherPanel({ courseGuid, segmentGuid, bare = false, onChanged }) {
  const { questions, loading, error, answer } = usePreClassQuestions({
    courseGuid,
    segmentGuid,
  })

  const handleAnswer = async (...args) => {
    const result = await answer(...args)
    if (typeof onChanged === "function") onChanged()
    return result
  }

  const unansweredCount = questions.filter((q) => q.status !== "ANSWERED").length
  const sorted = [...questions].sort((a, b) => {
    if (a.status !== b.status) return a.status === "ANSWERED" ? 1 : -1
    return new Date(b.created_date) - new Date(a.created_date)
  })

  // Above a segment this hides itself to keep the view clean. In a pre-class
  // tab there is nothing else on screen, so it says why it is empty instead.
  if (!loading && !error && questions.length === 0) {
    if (!bare) return null
    return (
      <Text fontSize="13px" fontWeight="300" color={c.textSub} py="20px" textAlign="center">
        No questions from your students yet.
      </Text>
    )
  }

  const shell = bare
    ? {}
    : {
        borderRadius: "12px",
        border: "1px solid",
        borderColor: c.borderSoft,
        background: c.surfaceSubtle,
        padding: "16px",
        mb: "20px",
      }

  return (
    <Box {...shell}>
      <HStack justify="space-between" mb="10px">
        <Text fontSize="13px" fontWeight="600" color={c.text}>
          Student questions
        </Text>
        {unansweredCount > 0 && (
          <Box
            background={c.accentSoft}
            color={c.accentSoftText}
            fontSize="10.5px"
            fontWeight="600"
            px="8px"
            py="2px"
            borderRadius="full"
          >
            {unansweredCount} unanswered
          </Box>
        )}
      </HStack>

      {loading ? (
        <Flex justify="center" py="14px">
          <Spinner size="sm" color={c.accent} />
        </Flex>
      ) : error ? (
        <Text fontSize="12px" color={c.textMuted}>
          {error}
        </Text>
      ) : (
        <VStack align="stretch" spacing="8px" maxHeight={bare ? "420px" : "260px"} overflowY="auto" pr="4px">
          {sorted.map((q) => (
            <QuestionBubble key={q.guid} question={q} isInstructor onAnswer={handleAnswer} />
          ))}
        </VStack>
      )}
    </Box>
  )
}
